import { ProductSetItemType, ProductEanType } from 'src/types/product';
import { ProductOrderType } from 'src/types/productDetail';

/** Order statuses considered "unprocessed" for product type change / set change warning. */
export const PENDING_ORDER_STATUSES = [
    'preReceived',
    'waitingForPayment',
    'waitingForShipment',
    'productsNotAvailable',
    'incorrect',
    'suspended',
    'processing',
    'preOrder'
] as const;

export type PendingOrderStatus = (typeof PENDING_ORDER_STATUSES)[number];

export type ProductTypeChangeKind = 'set' | 'service';

/**
 * Returns change kind when product type changes to Set (pack) or Service (serviceProduct).
 * Other type changes return null.
 */
export function getProductTypeChangeKind(
    originalType: string | null | undefined,
    newType: string | null | undefined
): ProductTypeChangeKind | null {
    if (!newType || newType === originalType) {
        return null;
    }
    if (newType === 'pack') {
        return 'set';
    }
    if (newType === 'serviceProduct') {
        return 'service';
    }
    return null;
}

/**
 * Compares two product sets by content (SKU + count), ignoring order.
 * Returns true if the sets differ.
 */
export function hasProductSetChanged(
    originalSet: ProductSetItemType[] | undefined | null,
    editedSet: ProductSetItemType[] | undefined | null
): boolean {
    const a = normalizeSetForCompare(originalSet);
    const b = normalizeSetForCompare(editedSet);
    if (a.length !== b.length) return true;
    const setA = new Set(a);
    for (const item of b) {
        if (!setA.has(item)) return true;
    }
    return false;
}

function normalizeSetForCompare(set: ProductSetItemType[] | undefined | null): string[] {
    if (!set || !Array.isArray(set)) return [];
    return set.map((item) => `${String(item.PRODUCT_SKU)}:${Number(item.PRODUCT_COUNT)}`).sort();
}

/**
 * Filters orders to those with status in the given list.
 */
export function getPendingOrdersForStatuses(
    orders: ProductOrderType[] | null | undefined,
    statuses: readonly string[]
): ProductOrderType[] {
    if (!orders || !Array.isArray(orders)) return [];
    const statusSet = new Set(statuses);
    return orders.filter((order) => order.STATUS && statusSet.has(order.STATUS));
}

/**
 * Format order for display in warning dialog (ESHOP_ORDER_ID or ID).
 */
export function formatOrderIdentifier(order: ProductOrderType): string {
    if (order.ESHOP_ORDER_ID) return String(order.ESHOP_ORDER_ID);
    return String(order.ID);
}

/** Mapuje EANS z editData na payload pro Core create/update. */
export function mapEansForSave(eans: ProductEanType[] | undefined | null): Array<{ ean: string; active: boolean }> | undefined {
    if (!eans) {
        return undefined;
    }
    return eans
        .map((item) => ({
            ean: String(item.ean || '').trim(),
            active: !!item.active
        }))
        // comma-joined legacy string nesmí skončit jako jeden EAN
        .filter((item) => item.ean && !item.ean.includes(','));
}

/** True když je EAN v rámci produktu duplicitní (trim + case-sensitive). */
export function hasDuplicateEanInList(eans: ProductEanType[] | undefined | null, candidate: string): boolean {
    const value = candidate.trim();
    if (!value) {
        return false;
    }
    return (eans || []).some((item) => item.ean === value);
}

export type LinkedEshopProvider = {
    ESHOP_KEY?: string;
    COMPANY_NAME?: string;
};

/** Maps changelog eshop key to current provider company name; unknown/empty name keeps the key. */
export function resolveLinkedEshopName(
    eshopKey: string | number | boolean | null,
    providers: LinkedEshopProvider[] | undefined | null
): string {
    const key = eshopKey == null ? '' : String(eshopKey);
    if (!key) {
        return key;
    }
    const name = (providers ?? []).find((item) => item.ESHOP_KEY === key)?.COMPANY_NAME?.trim();
    return name || key;
}
